import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from "@/api/base44Client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  BookOpen, 
  User, 
  ChevronDown, 
  ChevronRight,
  Target,
  MessageSquare,
  Zap,
  Shield,
  Battery,
  TrendingUp
} from 'lucide-react';
import { cn } from "@/lib/utils";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend
} from 'recharts';

const sections = [
  {
    id: 'alignment',
    title: 'Why I\'m Here & How Hard I Push',
    icon: Target,
    color: 'indigo',
    questions: [
      { key: 'drives', label: 'What drives me' },
      { key: 'performance', label: 'High performance means' },
      { key: 'non_negotiables', label: 'Non-negotiables' }
    ]
  },
  {
    id: 'value',
    title: 'Where I Create Outsized Value',
    icon: Zap,
    color: 'emerald',
    questions: [
      { key: 'strengths', label: 'Top strengths' },
      { key: 'overperform', label: 'I overperform at' },
      { key: 'drains', label: 'Drains me' }
    ]
  },
  {
    id: 'communication',
    title: 'Communication & Feedback',
    icon: MessageSquare,
    color: 'cyan',
    questions: [
      { key: 'reach', label: 'Best way to reach me' },
      { key: 'urgent', label: 'What "urgent" means' },
      { key: 'feedback', label: 'Feedback I respond to' },
      { key: 'stress', label: 'When stressed, I...' }
    ]
  },
  {
    id: 'decisions',
    title: 'Decisions & Ownership',
    icon: Shield,
    color: 'amber',
    questions: [
      { key: 'framing', label: 'Decision framing' },
      { key: 'input', label: 'When I want input' },
      { key: 'ownership', label: 'Ownership means' },
      { key: 'issues', label: 'Raise issues by...' }
    ]
  },
  {
    id: 'conflict',
    title: 'Conflict & Trust',
    icon: Shield,
    color: 'rose',
    questions: [
      { key: 'disagree', label: 'Best way to disagree' },
      { key: 'shuts_down', label: 'What shuts me down' },
      { key: 'respect', label: 'Earns my respect' }
    ]
  },
  {
    id: 'energy',
    title: 'Energy & Sustainability',
    icon: Battery,
    color: 'purple',
    questions: [
      { key: 'best', label: 'When I\'m at my best' },
      { key: 'overload', label: 'Signs of overload' },
      { key: 'reset', label: 'What helps me reset' }
    ]
  },
  {
    id: 'improvement',
    title: 'How to Make Me Better',
    icon: TrendingUp,
    color: 'orange',
    questions: [
      { key: 'improve', label: 'Fastest way to help me' },
      { key: 'misunderstood', label: 'Often misunderstood' },
      { key: 'working_on', label: 'Working to change' }
    ]
  }
];

const scoringDimensions = [
  { key: 'intensity', label: 'Intensity', fullLabel: 'Intensity Tolerance' },
  { key: 'ownership', label: 'Ownership', fullLabel: 'Ownership Bias' },
  { key: 'speed', label: 'Speed', fullLabel: 'Speed vs Polish' },
  { key: 'directness', label: 'Directness', fullLabel: 'Directness' },
  { key: 'adaptability', label: 'Adapt', fullLabel: 'Adaptability' },
  { key: 'endurance', label: 'Endurance', fullLabel: 'Endurance' }
];

export default function OfficerUserManualDialog({ officer, open, onClose, currentUserProfile }) {
  const [expandedSection, setExpandedSection] = useState(null);
  
  const userManual = officer?.user_manual || {};
  const currentUserManual = currentUserProfile?.user_manual || {};
  
  // Build radar chart data
  const radarData = scoringDimensions.map(dim => ({
    dimension: dim.label,
    fullLabel: dim.fullLabel,
    officer: userManual.scoring?.[dim.key] || 0,
    you: currentUserManual.scoring?.[dim.key] || 0
  }));
  
  const hasOfficerScoring = Object.values(userManual.scoring || {}).some(v => v > 0);
  const hasYourScoring = Object.values(currentUserManual.scoring || {}).some(v => v > 0);
  const showRadar = hasOfficerScoring || hasYourScoring;

  if (!officer) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="civ-officerusermanualdialog-dialog" className="bg-[#0B1221] border-slate-800 text-slate-100 max-w-2xl max-h-[85vh] p-0">
        <DialogHeader className="p-6 pb-4 border-b border-slate-800">
          <DialogTitle className="flex items-center gap-3">
            <div className="relative">
              {officer.avatar_url ? (
                <img src={officer.avatar_url} alt={officer.callsign} className="w-12 h-12 rounded-full object-cover border-2 border-slate-700" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center text-slate-400 font-bold text-lg">
                  {officer.callsign?.substring(0, 2).toUpperCase()}
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="text-xl font-bold">{officer.callsign}'s User Manual</div>
              <div className="text-xs text-slate-400 font-mono uppercase">{officer.rank_title} • How to work effectively with me</div>
            </div>
            <BookOpen className="w-8 h-8 text-emerald-400" />
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[calc(85vh-120px)]">
          <div className="p-6 space-y-6">
            {/* Radar Chart Section */}
            {showRadar && (
              <div className="bg-slate-900/50 rounded-lg border border-slate-800 p-4">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Target className="w-4 h-4 text-indigo-400" />
                  Self-Assessment Comparison
                </h3>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                      <PolarGrid stroke="#334155" />
                      <PolarAngleAxis 
                        dataKey="dimension" 
                        tick={{ fill: '#94a3b8', fontSize: 11 }}
                      />
                      <PolarRadiusAxis 
                        angle={30} 
                        domain={[0, 5]} 
                        tick={{ fill: '#64748b', fontSize: 10 }}
                        tickCount={6}
                      />
                      {hasOfficerScoring && (
                        <Radar
                          name={officer.callsign}
                          dataKey="officer"
                          stroke="#818cf8"
                          fill="#818cf8"
                          fillOpacity={0.3}
                          strokeWidth={2}
                        />
                      )}
                      {hasYourScoring && currentUserProfile?.user_email !== officer.user_email && (
                        <Radar
                          name="You"
                          dataKey="you"
                          stroke="#34d399"
                          fill="#34d399"
                          fillOpacity={0.2}
                          strokeWidth={2}
                        />
                      )}
                      <Legend 
                        wrapperStyle={{ paddingTop: '10px' }}
                        formatter={(value) => <span className="text-xs text-slate-300">{value}</span>}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Scoring Legend */}
                <div className="mt-4 grid grid-cols-3 gap-2 text-[10px]">
                  {scoringDimensions.map(dim => (
                    <div key={dim.key} className="flex items-center justify-between bg-slate-800/50 rounded px-2 py-1">
                      <span className="text-slate-400">{dim.fullLabel}</span>
                      <div className="flex gap-2">
                        {hasOfficerScoring && (
                          <Badge className="bg-indigo-500/20 text-indigo-300 h-4 px-1.5">
                            {userManual.scoring?.[dim.key] || '-'}
                          </Badge>
                        )}
                        {hasYourScoring && currentUserProfile?.user_email !== officer.user_email && (
                          <Badge className="bg-emerald-500/20 text-emerald-300 h-4 px-1.5">
                            {currentUserManual.scoring?.[dim.key] || '-'}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* User Manual Sections */}
            <div className="space-y-2">
              {sections.map(section => {
                const sectionData = userManual[section.id] || {};
                const hasContent = Object.values(sectionData).some(v => v);
                const isExpanded = expandedSection === section.id;
                const Icon = section.icon;
                
                return (
                  <div key={section.id} className="border border-slate-800 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                      className={cn(
                        "w-full p-3 flex items-center justify-between transition-colors",
                        isExpanded ? "bg-slate-800/80" : "bg-slate-900/50 hover:bg-slate-800/50"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className={cn("w-4 h-4", `text-${section.color}-400`)} />
                        <span className="text-sm font-medium text-slate-200">{section.title}</span>
                        {!hasContent && (
                          <Badge variant="outline" className="text-[9px] h-4 border-slate-700 text-slate-500">
                            Not filled
                          </Badge>
                        )}
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                      )}
                    </button>
                    
                    {isExpanded && (
                      <div className="p-4 bg-slate-900/30 space-y-3">
                        {section.questions.map(question => {
                          const answer = sectionData[question.key];
                          return (
                            <div key={question.key}>
                              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">
                                {question.label}
                              </div>
                              <div className={cn(
                                "text-sm",
                                answer ? "text-slate-300" : "text-slate-600 italic"
                              )}>
                                {answer || 'Not answered'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}